import CloseIcon from '@mui/icons-material/Close'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import type { ReactNode } from 'react'

import { spacing } from '@/themes/tokens'

import { Box } from '../Box'

// Pulled in to align with the horizontal padding of the title/content/actions
// sections, so the rules stop short of the dialog edge instead of spanning it.
const InsetDivider = styled(Divider)(({ theme }) => ({
  marginLeft: theme.spacing(spacing.lg),
  marginRight: theme.spacing(spacing.lg),
}))

export interface ModalProps {
  onClose: () => void
  title: string
  children: ReactNode
  actions: ReactNode
  /** Rendered under the title, inside the header — e.g. a wizard progress bar. */
  headerContent?: ReactNode
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  /**
   * Makes the modal non-dismissable: the close button, the backdrop, and Escape
   * all stop working. Set this while a write is in flight.
   */
  disableClose?: boolean
}

/**
 * Shared dialog shell.
 *
 * Takes no `open` prop by design — callers mount and unmount it conditionally,
 * which keeps its internal state (form values, wizard step) tied to its lifetime
 * instead of needing a manual reset on every close.
 */
export const Modal = ({
  onClose,
  title,
  children,
  actions,
  headerContent,
  maxWidth = 'sm',
  disableClose = false,
}: ModalProps) => (
  <Dialog open onClose={disableClose ? undefined : onClose} maxWidth={maxWidth} fullWidth>
    <DialogTitle>
      <Box variant='flexBetween'>
        <Typography variant='h6'>{title}</Typography>
        <IconButton size='small' onClick={onClose} disabled={disableClose} aria-label='Close'>
          <CloseIcon fontSize='small' />
        </IconButton>
      </Box>
      {headerContent}
    </DialogTitle>
    <InsetDivider />
    <DialogContent>{children}</DialogContent>
    <InsetDivider />
    <DialogActions>{actions}</DialogActions>
  </Dialog>
)
